import { keyframes } from '@emotion/react';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { GridColDef } from '@mui/x-data-grid';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { getClaims, getExplorerUrl } from '@/core/services/event-admin';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

interface FeeRow {
  id: string;
  bib: string;
  date: Date;
  txHash: string;
  txFee: number;
  chain: string;
}

export function FeePage() {
  const { raceAlias } = useParams<{ raceAlias: string }>();
  const theme = useTheme();
  const adminGradient = theme.custom?.adminGradient || theme.custom?.gradient || theme.palette.primary.main;

  const [feeRows, setFeeRows] = useState<FeeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchClaims = useCallback(async () => {
    if (!raceAlias) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getClaims(raceAlias);
      // txHash と txFee があるもの（ミント完了済み）のみ
      const completedClaims = data.filter((claim) => claim.txHash && claim.txFee !== null && claim.txFee !== undefined);

      const rows = completedClaims.map((claim) => ({
        id: claim.id,
        bib: claim.bib,
        date: claim.completedAt?.toDate() || claim.createdAt.toDate(),
        txHash: claim.txHash || '',
        txFee: claim.txFee || 0,
        chain: claim.chain,
      }));

      setFeeRows(rows);
    } catch (err) {
      console.error('Failed to fetch claims:', err);
      setError('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [raceAlias]);

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  const handleDownloadCsv = () => {
    const headers = ['ゼッケン', '日付', 'トランザクションID', 'ガス代（ETH）'];
    const csvRows = feeRows.map((row) => [row.bib, row.date.toLocaleString('ja-JP'), row.txHash, row.txFee.toFixed(8)]);

    const csvContent = [headers.join(','), ...csvRows.map((row) => row.join(','))].join('\n');

    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
    const blob = new Blob([bom, csvContent], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `fee_${raceAlias}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const columns: GridColDef<FeeRow>[] = [
    { field: 'bib', headerName: 'ゼッケン', width: 100 },
    {
      field: 'date',
      headerName: '日付',
      width: 180,
      valueFormatter: (value: Date) => value.toLocaleString('ja-JP'),
    },
    {
      field: 'txHash',
      headerName: 'トランザクション',
      width: 300,
      renderCell: (params) => {
        const row = params.row as FeeRow;
        return (
          <Link
            href={getExplorerUrl(row.chain, row.txHash)}
            target="_blank"
            rel="noopener"
            sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
          >
            {row.txHash.slice(0, 10)}...{row.txHash.slice(-8)}
          </Link>
        );
      },
    },
    {
      field: 'txFee',
      headerName: 'ガス代（ETH）',
      width: 180,
      type: 'number',
      valueFormatter: (value: number) => value.toFixed(8),
    },
  ];

  const totalFeeETH = feeRows.reduce((sum, row) => sum + row.txFee, 0);

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3, animation: `${fadeInUp} 0.6s ease-out` }}>
          {error}
        </Alert>
      )}

      {/* 統計カード */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          animation: `${fadeInUp} 0.6s ease-out`,
        }}
      >
        {/* メイン統計 + コントロール */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {/* 統計ボックス */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, flexGrow: 1 }}>
            <Box
              sx={{
                minWidth: 120,
                px: 2,
                py: 1,
                borderRadius: '8px',
                backgroundColor: 'action.hover',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                トランザクション数
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {feeRows.length}
              </Typography>
            </Box>
            <Box
              sx={{
                minWidth: 160,
                px: 2,
                py: 1,
                borderRadius: '8px',
                backgroundColor: 'info.main',
                color: 'info.contrastText',
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                合計ガス代（ETH）
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {totalFeeETH.toFixed(6)}
              </Typography>
            </Box>
          </Box>

          {/* コントロール */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadCsv}
              disabled={feeRows.length === 0}
              sx={{
                borderRadius: '16px',
                border: '2px solid transparent',
                backgroundImage: `linear-gradient(#fff, #fff), ${adminGradient}`,
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
                color: 'text.primary',
                '&:hover': {
                  backgroundImage: `linear-gradient(#f5f5f5, #f5f5f5), ${adminGradient}`,
                },
                '&.Mui-disabled': {
                  backgroundImage: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                },
              }}
            >
              CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchClaims}
              sx={{
                borderRadius: '16px',
                border: '2px solid transparent',
                backgroundImage: `linear-gradient(#fff, #fff), ${adminGradient}`,
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
                color: 'text.primary',
                '&:hover': {
                  backgroundImage: `linear-gradient(#f5f5f5, #f5f5f5), ${adminGradient}`,
                },
              }}
            >
              更新
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* データグリッド */}
      <Paper
        sx={{
          height: 600,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          animation: `${fadeInUp} 0.6s ease-out 0.2s both`,
          '& .MuiDataGrid-root': {
            border: 'none',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'action.hover',
            borderBottom: '2px solid',
            borderColor: 'divider',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <DataGrid
          rows={feeRows}
          columns={columns}
          loading={loading}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
        />
      </Paper>
    </Box>
  );
}
